import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  buyerSubmissionsApi,
  type BuyerIntroEmailStateDto,
} from "@/api/buyers";
import { useToast } from "@/design-system/components/ui";
import { buyerKeys, buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";
import { withListingPages } from "@/lib/listingPageUrl";
import { SendPreviewModal } from "../sendPreview/SendPreviewModal";
import { useSenderIdentity } from "../sendPreview/useSenderIdentity";

/**
 * The Phoenix send — one confirmation naming the recipient, with the message
 * itself as the last thing on screen before it goes.
 *
 * <p>🔴 <b>The recipient is stated, not implied.</b> The submission page also
 * hosts the package panel, whose send goes to the LISTING BROKER; this one goes
 * to the buyer. A confirmation that said only "Send?" would be the one place a
 * specialist could act on the wrong audience without a word of warning.</p>
 *
 * <p>There is no address field, deliberately: this email has exactly one
 * correct recipient. A buyer with no address on file is refused upstream — the
 * panel's Send button is disabled and the server's own sentence, which offers
 * the own-client path instead, is rendered beside it.</p>
 *
 * <p>The screen itself is now the SHARED send preview, so this file holds only
 * what is particular to the intro email: who it goes to, that it is not the
 * package, and what sending does. The identity block and the read-only body come
 * from the shared component, which is where the package send and the seller
 * invitation get them too.</p>
 */
export function IntroEmailSendModal({
  submissionId,
  email,
  includeListingLink,
  onSent,
  onClose,
}: {
  submissionId: string;
  email: BuyerIntroEmailStateDto;
  /** R3 — the panel's send-time choice. The preview shows the line when it is
   *  on, and the request carries it, so the screen and the send cannot differ. */
  includeListingLink: boolean;
  /** Called once the send succeeds — the tab's collapse after a send (J6). */
  onSent?: () => void;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const identity = useSenderIdentity();

  const send = useMutation({
    mutationFn: () => buyerSubmissionsApi.sendIntroEmail(submissionId, { includeListingLink }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      // The send stamps the buyer's contact clocks too, so the buyer's own
      // reads go stale with it — a prefix wider than the submission's.
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
      toast.success(`Sent to ${r.toEmail}.`);
      onSent?.();
      onClose();
    },
    // Every refusal happens before the transport call, so an error here always
    // means nothing left the building — the message can be shown as-is.
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <SendPreviewModal
      title="Send the intro email to the buyer"
      onClose={onClose}
      toName={email.buyerName}
      toEmail={email.buyerEmail}
      audienceNote="under your name. It does not go to the listing broker."
      identity={identity}
      subject={email.subject}
      body={
        includeListingLink
          ? withListingPages(email.bodyDraft ?? "", email.listingPageLine)
          : email.bodyDraft
      }
      confirmLabel="Send from Phoenix"
      confirmTestId="intro-email-send-confirm"
      onConfirm={() => send.mutate()}
      pending={send.isPending}
      footnote={
        <>
          Your signature is added automatically. Sending records this
          submission&apos;s intro email as sent; it does not change the
          submission&apos;s status and it does not send the package.
        </>
      }
    />
  );
}
